/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */

import { useState, useEffect, useMemo } from 'react';
import MaterialReactTable from 'material-react-table';
import {
  Box,
  Card,
  Container,
  Grid,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Stack,
  Paper,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';

import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import Iconify from '../../../components/Iconify';
import axiosInstance from '../../../utils/axios';

const COMMITMENT_STATEMENTS = [
  'I can attend the mandatory live orientation on Sunday, October 4, 2026, at 5:30 p.m. Central Time with my camera on.',
  'I can attend at least four of the six virtual learning sessions, including orientation.',
  'I understand that the cohort year runs from October 1, 2026, through September 30, 2027.',
  'I understand that the $35 application fee is nonrefundable and does not guarantee acceptance.',
  'If accepted, I agree to submit the $75 Founding Member investment by the stated deadline.',
  'I understand that membership is granted to the individual applicant and is nontransferable.',
  'I agree to participate actively, communicate professionally, and engage respectfully with fellow cohort members.',
  'I certify that the information provided in this application is complete and accurate.',
];

export default function NbclnApplications() {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ total: 0, paid: 0, incomplete: 0, revenue: 0 });
  
  // Selected application for detail modal
  const [selectedApp, setSelectedApp] = useState(null);
  const [openDetailModal, setOpenDetailModal] = useState(false);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/admin/get/nbcln-applications');
      if (res?.data?.status) {
        const apps = res?.data?.data || [];
        setData(apps);
        
        const paidApps = apps.filter((item) => item.payment_status === 'paid');
        const incompleteApps = apps.filter((item) => item.payment_status !== 'paid');
        
        setCounts({
          total: apps.length,
          paid: paidApps.length,
          incomplete: incompleteApps.length,
          revenue: paidApps.length * 35,
        });
      }
    } catch (err) {
      console.error('Fetch NBCLN applications error:', err);
      enqueueSnackbar(err?.message || 'Failed to load NBCLN applications', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleOpenDetail = (row) => {
    setSelectedApp(row);
    setOpenDetailModal(true);
  };

  const handleCloseDetail = () => {
    setSelectedApp(null);
    setOpenDetailModal(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await axiosInstance.delete(`/api/admin/del/nbcln-application/${deleteId}`);
      if (res?.data?.status) {
        enqueueSnackbar('Application deleted successfully', { variant: 'success' });
        fetchApplications();
      } else {
        enqueueSnackbar(res?.data?.message || 'Delete failed', { variant: 'error' });
      }
    } catch (err) {
      console.error('Delete application error:', err);
      enqueueSnackbar(err?.message || 'Failed to delete application', { variant: 'error' });
    } finally {
      setOpenDeleteDialog(false);
      setDeleteId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'App ID',
        size: 80,
        Cell: ({ cell }) => <strong>#{cell.getValue()}</strong>,
      },
      {
        accessorKey: 'created_at',
        header: 'Applied Date',
        size: 130,
        Cell: ({ cell }) => {
          const val = cell.getValue();
          if (!val) return '—';
          return new Date(val).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });
        },
      },
      {
        accessorKey: 'full_name_and_title',
        header: 'Applicant Name & Title',
        size: 200,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {row.original.full_name_and_title || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.leadership_category || 'Established Leader'}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email & Phone',
        size: 200,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2">{row.original.email || '—'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.phone || '—'}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: 'amount',
        header: 'Fee',
        size: 90,
        Cell: () => <Typography variant="subtitle2" sx={{ color: '#b48e35', fontWeight: 700 }}>$35.00</Typography>,
      },
      {
        accessorKey: 'payment_status',
        header: 'Payment Status',
        size: 140,
        Cell: ({ cell }) => {
          const status = cell.getValue();
          const isPaid = status === 'paid';
          return (
            <Chip
              label={isPaid ? 'Paid ($35)' : 'Incomplete'}
              color={isPaid ? 'success' : 'warning'}
              size="small"
              icon={isPaid ? <CheckCircleIcon /> : <HourglassEmptyIcon />}
              sx={{ fontWeight: 600 }}
            />
          );
        },
      },
      {
        accessorKey: 'payment_method',
        header: 'Method',
        size: 110,
        Cell: ({ cell }) => {
          const method = cell.getValue();
          if (!method) return '—';
          return (
            <Chip
              label={method.toUpperCase()}
              variant="outlined"
              size="small"
              sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}
            />
          );
        },
      },
      {
        accessorKey: 'transaction_id',
        header: 'Transaction Ref',
        size: 170,
        Cell: ({ cell }) => {
          const val = cell.getValue();
          if (!val) return <span style={{ color: '#999' }}>None</span>;
          return (
            <Tooltip title={val}>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  display: 'inline-block',
                  maxWidth: 150,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {val}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 110,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="View Full Application Details">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleOpenDetail(row.original)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Application">
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setDeleteId(row.original.id);
                  setOpenDeleteDialog(true);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <Page title="NBCLN Applications | Admin">
      <Container maxWidth="xl">
        <HeaderBreadcrumbs
          heading="NBCLN Applications & Payments"
          links={[
            { name: 'Dashboard', href: '/dashboard/home' },
            { name: 'NBCLN Applications', href: '/dashboard/nbcln' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchApplications}
              disabled={loading}
              sx={{ backgroundColor: '#1e293b', '&:hover': { backgroundColor: '#0f172a' } }}
            >
              Refresh
            </Button>
          }
        />

        {/* ─── SUMMARY METRICS CARDS ─── */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, display: 'flex', alignItems: 'center', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#e0e7ff', color: '#3730a3', mr: 2 }}>
                <AssignmentIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{counts.total}</Typography>
                <Typography variant="body2" color="text.secondary">Total Applications</Typography>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, display: 'flex', alignItems: 'center', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#dcfce7', color: '#166534', mr: 2 }}>
                <CheckCircleIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#15803d' }}>{counts.paid}</Typography>
                <Typography variant="body2" color="text.secondary">Paid ($35 Fee Completed)</Typography>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, display: 'flex', alignItems: 'center', bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fef3c7', color: '#92400e', mr: 2 }}>
                <AttachMoneyIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#b45309' }}>${counts.revenue}</Typography>
                <Typography variant="body2" color="text.secondary">Total Application Revenue</Typography>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, display: 'flex', alignItems: 'center', bgcolor: '#fff1f2', border: '1px solid #fecdd3' }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#ffe4e6', color: '#9f1239', mr: 2 }}>
                <HourglassEmptyIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#be123c' }}>{counts.incomplete}</Typography>
                <Typography variant="body2" color="text.secondary">Incomplete (Unpaid)</Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* ─── MATERIAL REACT TABLE ─── */}
        <Card sx={{ p: 2 }}>
          <MaterialReactTable
            columns={columns}
            data={data}
            state={{ isLoading: loading }}
            enableColumnResizing
            enableSorting
            enablePagination
            enableGlobalFilter
            muiTablePaperProps={{
              elevation: 0,
              sx: { borderRadius: '12px' },
            }}
          />
        </Card>

        {/* ─── APPLICATION DETAIL DIALOG MODAL ─── */}
        <Dialog
          open={openDetailModal}
          onClose={handleCloseDetail}
          maxWidth="md"
          fullWidth
          scroll="paper"
        >
          <DialogTitle sx={{ m: 0, p: 2.5, bgcolor: '#0f172a', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ color: '#d4a84b', fontWeight: 700 }}>
                NBCLN Application #{selectedApp?.id}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                National Black Cotillion Leaders Network (2026–2027 Founding Cohort)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDetail} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 3 }}>
            {selectedApp && (
              <Stack spacing={3}>
                {/* 1. Payment & Fee Snapshot */}
                <Paper sx={{ p: 2.5, bgcolor: selectedApp.payment_status === 'paid' ? '#f0fdf4' : '#fffbeb', borderRadius: 2, border: `1px solid ${selectedApp.payment_status === 'paid' ? '#86efac' : '#fde047'}` }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Payment Status</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedApp.payment_status === 'paid' ? 'PAID ($35.00 USD)' : 'INCOMPLETE (PENDING PAYMENT)'}
                          color={selectedApp.payment_status === 'paid' ? 'success' : 'warning'}
                          sx={{ fontWeight: 700 }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Payment Gateway</Typography>
                      <Typography variant="subtitle2" sx={{ textTransform: 'uppercase' }}>
                        {selectedApp.payment_method || 'Stripe'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Transaction Reference</Typography>
                      <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                        {selectedApp.transaction_id || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* 2. Applicant Identification */}
                <Box>
                  <Typography variant="subtitle1" sx={{ color: '#1e293b', fontWeight: 700, mb: 1.5, pb: 0.5, borderBottom: '2px solid #e2e8f0' }}>
                    1. Applicant Identification
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Full Name and Title</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedApp.full_name_and_title || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Leadership Category</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#b48e35' }}>{selectedApp.leadership_category || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Email Address</Typography>
                      <Typography variant="body2">{selectedApp.email || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                      <Typography variant="body2">{selectedApp.phone || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">City, State & Country</Typography>
                      <Typography variant="body2">{selectedApp.residence || '—'}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* 3. Program & Experience Details */}
                <Box>
                  <Typography variant="subtitle1" sx={{ color: '#1e293b', fontWeight: 700, mb: 1.5, pb: 0.5, borderBottom: '2px solid #e2e8f0' }}>
                    2. Organization & Program Information
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Organization / Program Details</Typography>
                      <Paper sx={{ p: 1.5, bgcolor: '#f8fafc', mt: 0.5, borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedApp.organization_details || 'No information provided.'}
                        </Typography>
                      </Paper>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">Current Cotillion Program / Planned Initiative</Typography>
                      <Paper sx={{ p: 1.5, bgcolor: '#f8fafc', mt: 0.5, borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedApp.program_description || 'No information provided.'}
                        </Typography>
                      </Paper>
                    </Box>
                  </Stack>
                </Box>

                {/* 4. Motivation & Contributions */}
                <Box>
                  <Typography variant="subtitle1" sx={{ color: '#1e293b', fontWeight: 700, mb: 1.5, pb: 0.5, borderBottom: '2px solid #e2e8f0' }}>
                    3. Statement of Interest & Contributions
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Why Founding Member?</Typography>
                      <Paper sx={{ p: 1.5, bgcolor: '#f8fafc', mt: 0.5, borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedApp.why_founding_member || 'No statement provided.'}
                        </Typography>
                      </Paper>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">Unique Perspective, Skills & Resources</Typography>
                      <Paper sx={{ p: 1.5, bgcolor: '#f8fafc', mt: 0.5, borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedApp.contributions || 'No information provided.'}
                        </Typography>
                      </Paper>
                    </Box>
                  </Stack>
                </Box>

                {/* 5. Commitments Checklist */}
                <Box>
                  <Typography variant="subtitle1" sx={{ color: '#1e293b', fontWeight: 700, mb: 1.5, pb: 0.5, borderBottom: '2px solid #e2e8f0' }}>
                    4. Required Cohort Commitments (8 of 8 Agreed)
                  </Typography>
                  <Stack spacing={1}>
                    {COMMITMENT_STATEMENTS.map((text, idx) => (
                      <Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start">
                        <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 18, mt: 0.3 }} />
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#334155' }}>
                          {text}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDetail} variant="outlined" color="inherit">
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.print()}
            >
              Print Details
            </Button>
          </DialogActions>
        </Dialog>

        {/* ─── DELETE CONFIRMATION DIALOG ─── */}
        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
          <DialogTitle sx={{ fontWeight: 700 }}>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Are you sure you want to permanently delete application #{deleteId}? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenDeleteDialog(false)} variant="outlined" color="inherit">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
